package com.gloopgo.game.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeDrawing
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.itemsIndexed
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import com.gloopgo.game.domain.BlobAiController
import com.gloopgo.game.engine.LeaderboardEntry
import com.gloopgo.game.moderation.BlockedPlayersStore
import com.gloopgo.game.moderation.PlayerReportReason
import com.gloopgo.game.moderation.visiblePlayerName
import com.gloopgo.game.network.MultiplayerRepository
import com.gloopgo.game.viewmodel.LeaderboardViewModel
import gloopgo.shared.generated.resources.*
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.koinInject

@Composable
fun LeaderboardScreen(
    onBackToMenu: () -> Unit,
    viewModel: LeaderboardViewModel
) {
    val uiState by viewModel.uiState.collectAsStateWithLifecycle()
    val blockedPlayersStore: BlockedPlayersStore = koinInject()
    val repository: MultiplayerRepository = koinInject()
    val blockedPlayers by blockedPlayersStore.blocked.collectAsStateWithLifecycle()
    val blockedIds = remember(blockedPlayers) { blockedPlayers.map { it.uid }.toSet() }
    val blockedLabel = stringResource(Res.string.blocked_player_name)
    val safetyScope = rememberCoroutineScope()
    var safetyTarget by remember { mutableStateOf<LeaderboardEntry?>(null) }
    val selfId = uiState.currentUserId
    fun shownName(entry: LeaderboardEntry) = visiblePlayerName(
        name = entry.name,
        playerId = entry.id,
        localPlayerId = selfId,
        blockedIds = blockedIds,
        blockedLabel = blockedLabel
    )
    fun selectPlayer(entry: LeaderboardEntry): (() -> Unit)? {
        if (entry.id.isEmpty() || selfId == null || entry.id == selfId) return null
        return { safetyTarget = entry }
    }

    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(
                Brush.verticalGradient(
                    listOf(
                        Color(0xFF0F172A),
                        Color(0xFF090D16)
                    )
                )
            )
    ) {
        Scaffold(
            containerColor = Color.Transparent,
            contentWindowInsets = WindowInsets.safeDrawing,
            topBar = {
                NeonAbyssTopAppBar(
                    title = stringResource(Res.string.leaderboard_title),
                    onBack = onBackToMenu,
                )
            },
        ) { innerPadding ->
        BoxWithConstraints(
            modifier = Modifier
                .fillMaxSize()
                .padding(innerPadding)
                .padding(horizontal = 16.dp, vertical = 12.dp),
            contentAlignment = Alignment.Center
        ) {
            val isExpanded = maxWidth > 720.dp
            val isCompactHeight = maxHeight < 560.dp

            if (isExpanded) {
                // Wide / Tablet / Desktop: Two-Pane Adaptive Layout
                Column(
                    modifier = Modifier
                        .widthIn(max = 920.dp)
                        .fillMaxWidth()
                        .verticalScroll(rememberScrollState()),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    Text(
                        text = stringResource(Res.string.leaderboard_title),
                        fontSize = 26.sp,
                        fontWeight = FontWeight.Black,
                        color = Color(0xFFFFD600),
                        letterSpacing = 1.sp
                    )
                    Spacer(modifier = Modifier.height(4.dp))
                    Text(
                        text = stringResource(Res.string.leaderboard_subtitle),
                        fontSize = 12.sp,
                        color = Color(0xFF94A3B8)
                    )

                    Spacer(modifier = Modifier.height(20.dp))

                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(20.dp),
                        verticalAlignment = Alignment.Top
                    ) {
                        // Left Pane: Champions Podium & Summary Card
                        Card(
                            colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface),
                            shape = RoundedCornerShape(24.dp),
                            modifier = Modifier.weight(0.9f),
                            elevation = CardDefaults.cardElevation(defaultElevation = 8.dp)
                        ) {
                            Column(
                                modifier = Modifier.padding(if (isCompactHeight) 18.dp else 24.dp),
                                horizontalAlignment = Alignment.CenterHorizontally
                            ) {
                                Text(
                                    text = stringResource(Res.string.leaderboard_champions_podium),
                                    fontSize = 16.sp,
                                    fontWeight = FontWeight.Bold,
                                    color = Color(0xFFFFD600)
                                )

                                Spacer(modifier = Modifier.height(16.dp))

                                val top3 = uiState.entries.take(3)
                                if (top3.isNotEmpty()) {
                                    Column(
                                        modifier = Modifier.fillMaxWidth(),
                                        verticalArrangement = Arrangement.spacedBy(10.dp)
                                    ) {
                                        top3.forEachIndexed { idx, entry ->
                                            PodiumCard(
                                                rank = idx + 1,
                                                entry = entry,
                                                displayName = shownName(entry),
                                                onSelect = selectPlayer(entry)
                                            )
                                        }
                                    }
                                } else {
                                    Box(
                                        modifier = Modifier
                                            .fillMaxWidth()
                                            .height(120.dp),
                                        contentAlignment = Alignment.Center
                                    ) {
                                        Text(
                                            text = if (uiState.isLoading) stringResource(Res.string.leaderboard_loading) else stringResource(Res.string.leaderboard_no_scores),
                                            color = Color(0xFF94A3B8),
                                            fontSize = 13.sp
                                        )
                                    }
                                }

                            }
                        }

                        // Right Pane: Full Scrollable Standings
                        Card(
                            colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface),
                            shape = RoundedCornerShape(24.dp),
                            modifier = Modifier.weight(1.1f),
                            elevation = CardDefaults.cardElevation(defaultElevation = 8.dp)
                        ) {
                            Column(
                                modifier = Modifier.padding(if (isCompactHeight) 18.dp else 24.dp)
                            ) {
                                Row(
                                    modifier = Modifier.fillMaxWidth(),
                                    horizontalArrangement = Arrangement.SpaceBetween,
                                    verticalAlignment = Alignment.CenterVertically
                                ) {
                                    Text(
                                        text = stringResource(Res.string.leaderboard_global_standings),
                                        fontSize = 16.sp,
                                        fontWeight = FontWeight.Bold,
                                        color = Color(0xFF00E5FF)
                                    )
                                    Text(
                                        text = stringResource(Res.string.leaderboard_player_count, uiState.entries.size),
                                        fontSize = 12.sp,
                                        color = Color(0xFF94A3B8)
                                    )
                                }

                                Spacer(modifier = Modifier.height(14.dp))

                                if (uiState.isLoading) {
                                    Box(
                                        modifier = Modifier.fillMaxWidth().height(260.dp),
                                        contentAlignment = Alignment.Center
                                    ) {
                                        CircularProgressIndicator(color = Color(0xFF00E5FF), modifier = Modifier.size(36.dp))
                                    }
                                } else if (uiState.entries.isEmpty()) {
                                    Box(
                                        modifier = Modifier.fillMaxWidth().height(260.dp),
                                        contentAlignment = Alignment.Center
                                    ) {
                                        Text(stringResource(Res.string.leaderboard_no_entries), color = Color(0xFF94A3B8), fontSize = 14.sp)
                                    }
                                } else {
                                    LazyColumn(
                                        modifier = Modifier
                                            .fillMaxWidth()
                                            .heightIn(min = 220.dp, max = 420.dp)
                                    ) {
                                        itemsIndexed(uiState.entries) { index, entry ->
                                            LeaderboardRowItem(
                                                index = index,
                                                entry = entry,
                                                displayName = shownName(entry),
                                                onSelect = selectPlayer(entry)
                                            )
                                            HorizontalDivider(color = Color(0x1AFFFFFF))
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            } else {
                // Compact screen (Mobile / Portrait / Narrow)
                val listHeight = (maxHeight - 210.dp).coerceIn(160.dp, 440.dp)

                Card(
                    colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface),
                    shape = RoundedCornerShape(24.dp),
                    modifier = Modifier
                        .widthIn(max = 520.dp)
                        .fillMaxWidth(),
                    elevation = CardDefaults.cardElevation(defaultElevation = 8.dp)
                ) {
                    Column(
                        modifier = Modifier.padding(if (isCompactHeight) 16.dp else 24.dp),
                        horizontalAlignment = Alignment.CenterHorizontally
                    ) {
                        Text(
                            text = stringResource(Res.string.leaderboard_title),
                            fontSize = if (isCompactHeight) 18.sp else 22.sp,
                            fontWeight = FontWeight.Black,
                            color = Color(0xFFFFD600),
                            letterSpacing = 1.sp
                        )
                        Text(
                            text = stringResource(Res.string.leaderboard_subtitle_compact),
                            fontSize = 11.sp,
                            color = Color(0xFF94A3B8)
                        )

                        Spacer(modifier = Modifier.height(if (isCompactHeight) 10.dp else 16.dp))

                        if (uiState.isLoading) {
                            Box(modifier = Modifier.height(listHeight), contentAlignment = Alignment.Center) {
                                CircularProgressIndicator(color = Color(0xFF00E5FF), modifier = Modifier.size(36.dp))
                            }
                        } else if (uiState.entries.isEmpty()) {
                            Box(modifier = Modifier.height(listHeight), contentAlignment = Alignment.Center) {
                                Text(stringResource(Res.string.leaderboard_no_scores_first), color = Color(0xFF94A3B8), fontSize = 14.sp)
                            }
                        } else {
                            LazyColumn(modifier = Modifier.height(listHeight)) {
                                itemsIndexed(uiState.entries) { index, entry ->
                                    LeaderboardRowItem(
                                        index = index,
                                        entry = entry,
                                        displayName = shownName(entry),
                                        onSelect = selectPlayer(entry)
                                    )
                                    HorizontalDivider(color = Color(0x1AFFFFFF))
                                }
                            }
                        }

                    }
                }
            }
        }

        safetyTarget?.let { entry ->
            if (BlobAiController.isAiPlayerId(entry.id)) {
                AiPlayerDialog(
                    playerName = entry.name,
                    onDismiss = { safetyTarget = null }
                )
            } else PlayerSafetyDialog(
                playerName = entry.name,
                isBlocked = entry.id in blockedIds,
                onDismiss = { safetyTarget = null },
                onReport = { reason: PlayerReportReason ->
                    val reporterUid = repository.getCurrentUserId().orEmpty()
                    repository.submitPlayerReport(
                        reporterUid = reporterUid,
                        reportedUid = entry.id,
                        reportedNickname = entry.name,
                        reason = reason.wireValue
                    )
                },
                onBlock = {
                    blockedPlayersStore.block(entry.id, entry.name)
                    safetyScope.launch {
                        val ownerUid = repository.getCurrentUserId().orEmpty()
                        if (ownerUid.isNotEmpty()) {
                            repository.setPlayerBlocked(ownerUid, entry.id, entry.name, blocked = true)
                        }
                    }
                },
                onUnblock = {
                    blockedPlayersStore.unblock(entry.id)
                    safetyScope.launch {
                        val ownerUid = repository.getCurrentUserId().orEmpty()
                        if (ownerUid.isNotEmpty()) {
                            repository.setPlayerBlocked(ownerUid, entry.id, entry.name, blocked = false)
                        }
                    }
                }
            )
        }
        }
    }
}

@Composable
private fun PodiumCard(
    rank: Int,
    entry: LeaderboardEntry,
    displayName: String,
    onSelect: (() -> Unit)?
) {
    val (badgeText, badgeColor, bgColor) = when (rank) {
        1 -> Triple("🥇 1ST", Color(0xFFFFD600), Color(0x33FFD600))
        2 -> Triple("🥈 2ND", Color(0xFFE2E8F0), Color(0x22E2E8F0))
        3 -> Triple("🥉 3RD", Color(0xFFCD7F32), Color(0x22CD7F32))
        else -> Triple("#$rank", Color(0xFF94A3B8), Color(0x1A0F172A))
    }

    Row(
        modifier = Modifier
            .fillMaxWidth()
            .background(bgColor, RoundedCornerShape(12.dp))
            .then(if (onSelect != null) Modifier.clickable(onClick = onSelect) else Modifier)
            .padding(horizontal = 14.dp, vertical = 10.dp),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically
    ) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            Text(
                text = badgeText,
                fontWeight = FontWeight.Black,
                fontSize = 13.sp,
                color = badgeColor
            )
            Spacer(modifier = Modifier.width(10.dp))
            Text(
                text = displayName,
                fontWeight = FontWeight.Bold,
                fontSize = 14.sp,
                color = MaterialTheme.colorScheme.onSurface,
                maxLines = 1
            )
        }
        Text(
            text = "${entry.score}",
            fontWeight = FontWeight.Black,
            fontSize = 15.sp,
            color = Color(0xFF00E5FF)
        )
    }
}

@Composable
private fun LeaderboardRowItem(
    index: Int,
    entry: LeaderboardEntry,
    displayName: String,
    onSelect: (() -> Unit)?
) {
    val rankColor = when (index) {
        0 -> Color(0xFFFFD600) // Gold
        1 -> Color(0xFFE2E8F0) // Silver
        2 -> Color(0xFFCD7F32) // Bronze
        else -> Color(0xFF94A3B8)
    }

    Row(
        modifier = Modifier
            .fillMaxWidth()
            .then(if (onSelect != null) Modifier.clickable(onClick = onSelect) else Modifier)
            .padding(vertical = 9.dp, horizontal = 4.dp),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically
    ) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            Text(
                text = "#${index + 1}",
                fontWeight = FontWeight.Black,
                fontSize = 14.sp,
                color = rankColor,
                modifier = Modifier.width(36.dp)
            )
            Text(
                text = displayName,
                fontWeight = if (index < 3) FontWeight.Bold else FontWeight.SemiBold,
                fontSize = 14.sp,
                color = MaterialTheme.colorScheme.onSurface,
                maxLines = 1
            )
        }
        Text(
            text = "${entry.score}",
            fontWeight = FontWeight.Bold,
            fontSize = 14.sp,
            color = Color(0xFF00E5FF)
        )
    }
}
